import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/constants";
import { TRACKS } from "@/lib/tracks";
import { localBusinessSchema, breadcrumbListSchema } from "@/lib/jsonld";
import { usePageContentOverrideMap } from "@/hooks/usePageContentOverrides";

const TrackServicesIndex = ({ trackKey, servicesData, meta, intro }) => {
    const track = TRACKS[trackKey];
    const overrideMap = usePageContentOverrideMap();
    const services = Object.values(servicesData).map((s) => {
        const ov = overrideMap[`service:${trackKey}:${s.slug}`];
        return ov?.hero_image ? { ...s, heroImage: ov.hero_image } : s;
    });
    const jsonLd = [
        localBusinessSchema(),
        breadcrumbListSchema([
            { name: track.label, path: track.home },
            { name: "Services", path: track.servicesIndex },
        ]),
    ];
    return (
        <>
            <Seo {...meta} path={track.servicesIndex} jsonLd={jsonLd} />

            <Section dark className="py-16 md:py-20" data-testid="trackservices-hero">
                <Container>
                    <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-4">
                        {track.label} · Services
                    </p>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-wide text-white max-w-4xl">
                        {intro.title}
                    </h1>
                    <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 font-light leading-relaxed">
                        {intro.body}
                    </p>
                </Container>
            </Section>

            <Section data-testid="trackservices-grid">
                <Container>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        {services.map((s) => (
                            <Link
                                key={s.slug}
                                to={`${track.servicesBase}/${s.slug}`}
                                className="group flex flex-col bg-white border border-brand-dark/10 overflow-hidden hover:border-brand-cyan transition-colors"
                                data-testid={`trackservices-card-${s.slug}`}
                            >
                                <div className="aspect-[16/10] overflow-hidden bg-brand-dark">
                                    <img
                                        src={s.heroImage}
                                        alt={s.name}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>
                                <div className="flex flex-1 flex-col p-6">
                                    <p className="text-[11px] uppercase tracking-[0.2em] text-brand-coral font-semibold mb-2">
                                        {s.category}
                                    </p>
                                    <h2 className="font-display text-2xl tracking-wide text-brand-dark leading-tight">
                                        {s.name}
                                    </h2>
                                    <p className="mt-2 text-sm text-brand-dark/70 leading-relaxed flex-1">
                                        {s.heroSubhead}
                                    </p>
                                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand-cyan group-hover:text-brand-coral transition-colors">
                                        Learn more
                                        <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </Container>
            </Section>

            <Section dark className="py-16" data-testid="trackservices-cta">
                <Container className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <h2 className="font-display text-3xl sm:text-4xl tracking-wide text-white leading-tight max-w-2xl">
                        Don't see your scope? We likely do it.
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <BrandButton asChild variant="primary" size="lg">
                            <Link to={track.contact}>
                                Get a Free Quote
                                <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                            </Link>
                        </BrandButton>
                        <BrandButton asChild variant="outline" tone="onDark" size="lg">
                            <a href={COMPANY.phoneHref}>
                                <Phone className="h-4 w-4" strokeWidth={2.25} />
                                Call {COMPANY.phone}
                            </a>
                        </BrandButton>
                    </div>
                </Container>
            </Section>
        </>
    );
};

export default TrackServicesIndex;
